import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Banknote, ShieldCheck, Trash2, Wallet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import {
  deletePayoutMethod,
  getWalletOverview,
  savePayoutMethod,
  PAYOUT_CURRENCIES,
  CRYPTO_ASSETS,
  type CryptoAsset,
  type PayoutCurrency,
} from "@/lib/wallets.functions";


/** Payout details are security-sensitive: saving or deleting requires a fresh email code. */
export function PayoutMethods({ email }: { email: string | undefined }) {
  const queryClient = useQueryClient();
  const overviewFn = useServerFn(getWalletOverview);
  const saveFn = useServerFn(savePayoutMethod);
  const deleteFn = useServerFn(deletePayoutMethod);

  const [methodType, setMethodType] = useState<"bank" | "crypto">("bank");
  const [form, setForm] = useState({
    label: "",
    accountHolder: "",
    bankName: "",
    iban: "",
    bicSwift: "",
    bankCountry: "",
    bankAddress: "",
    intermediaryBank: "",
    currency: "USD" as PayoutCurrency,
    cryptoAsset: "USDT" as CryptoAsset,
    cryptoAddress: "",
  });
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [verified, setVerified] = useState(false);


  const { data } = useQuery({ queryKey: ["wallet-overview"], queryFn: () => overviewFn() });
  const methods = data?.methods ?? [];

  const set = (k: keyof typeof form, v: string) => setForm((f) => ({ ...f, [k]: v }));

  const sendCode = useMutation({
    mutationFn: async () => {
      if (!email) throw new Error("Missing account email.");
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: { shouldCreateUser: false },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      setOtpSent(true);
      toast.success("We emailed you a 6-digit code.");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const verify = useMutation({
    mutationFn: async () => {
      if (!email) throw new Error("Missing account email.");
      const { error } = await supabase.auth.verifyOtp({ email, token: otp.trim(), type: "email" });
      if (error) throw error;
    },
    onSuccess: () => {
      setVerified(true);
      toast.success("Verified, you can save payout details for the next 15 minutes.");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const save = useMutation({
    mutationFn: () =>
      saveFn({
        data:
          methodType === "crypto"
            ? {
                methodType: "crypto" as const,
                label: form.label || undefined,
                cryptoAsset: form.cryptoAsset,
                cryptoAddress: form.cryptoAddress || undefined,
              }
            : {
                methodType: "bank" as const,
                label: form.label || undefined,
                accountHolder: form.accountHolder || undefined,
                bankName: form.bankName || undefined,
                iban: form.iban || undefined,
                bicSwift: form.bicSwift || undefined,
                bankCountry: form.bankCountry || undefined,
                bankAddress: form.bankAddress || undefined,
                intermediaryBank: form.intermediaryBank || undefined,
              },
      }),

    onSuccess: () => {
      toast.success("Payout method saved.");
      void queryClient.invalidateQueries({ queryKey: ["wallet-overview"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteFn({ data: { id } }),
    onSuccess: () => {
      toast.success("Payout method removed.");
      void queryClient.invalidateQueries({ queryKey: ["wallet-overview"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <section className="space-y-5 rounded-xl border bg-card p-5">
      <div>
        <h2 className="font-display text-lg font-bold">Payout method</h2>
        <p className="text-sm text-muted-foreground">
          Where we send your Earn wallet withdrawals. Changes require an email verification code.
        </p>
      </div>

      {methods.length > 0 && (
        <ul className="space-y-2">
          {methods.map((m) => (
            <li
              key={m.id}
              className="flex items-center justify-between rounded-lg border px-3 py-2 text-sm"
            >
              <span className="flex items-center gap-2">
                {m.method_type === "crypto" ? (
                  <Wallet className="size-4" />
                ) : (
                  <Banknote className="size-4" />
                )}
                {m.method_type === "crypto"
                  ? `${m.crypto_asset ?? "USDT"} ${m.crypto_network ?? "ERC20"} · ${
                      m.crypto_address ? `${m.crypto_address.slice(0, 6)}…${m.crypto_address.slice(-4)}` : ""
                    }`
                  : `${m.bank_name ?? "Bank"} · ${m.iban ? `••••${m.iban.slice(-4)}` : ""}`}
                {m.verified_at && <ShieldCheck className="size-4 text-emerald-600" />}
              </span>

              <Button
                variant="ghost"
                size="sm"
                onClick={() => verified ? remove.mutate(m.id) : toast.error("Verify with the email code first.")}
              >
                <Trash2 className="size-4" />
              </Button>
            </li>
          ))}
        </ul>
      )}

      <div className="flex gap-2">
        {(["bank", "crypto"] as const).map((t) => (
          <Button
            key={t}
            type="button"
            size="sm"
            variant={methodType === t ? "hot" : "outline"}
            onClick={() => setMethodType(t)}
          >
            {t === "bank" ? "Bank transfer (IBAN)" : "USDT / USDC (ERC20)"}
          </Button>
        ))}
      </div>

      {methodType === "bank" ? (
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Account holder" value={form.accountHolder} onChange={(v) => set("accountHolder", v)} />
          <Field label="Bank name" value={form.bankName} onChange={(v) => set("bankName", v)} />
          <Field label="IBAN" value={form.iban} onChange={(v) => set("iban", v)} />
          <Field label="BIC / SWIFT" value={form.bicSwift} onChange={(v) => set("bicSwift", v)} />
          <Field label="Bank country (2-letter)" value={form.bankCountry} onChange={(v) => set("bankCountry", v)} />
          <Field label="Bank address" value={form.bankAddress} onChange={(v) => set("bankAddress", v)} />
          <Field
            label="Intermediary bank (optional)"
            value={form.intermediaryBank}
            onChange={(v) => set("intermediaryBank", v)}
          />
          <div className="space-y-2">
            <Label>Payout currency</Label>
            <select
              className="h-10 w-full rounded-md border bg-background px-3 text-sm"
              value={form.currency}
              onChange={(e) => set("currency", e.target.value as PayoutCurrency)}
            >
              {PAYOUT_CURRENCIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground">
              Payouts are calculated in USD and sent in EUR, USD or AED to the IBAN above.
            </p>
          </div>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label>Stablecoin</Label>
            <select
              className="h-10 w-full rounded-md border bg-background px-3 text-sm"
              value={form.cryptoAsset}
              onChange={(e) => set("cryptoAsset", e.target.value as CryptoAsset)}
            >
              {CRYPTO_ASSETS.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <Label>Network</Label>
            <Input value="ERC20 (Ethereum)" readOnly disabled />
          </div>
          <div className="space-y-2 sm:col-span-2">
            <Label>Wallet address</Label>
            <Input
              value={form.cryptoAddress}
              onChange={(e) => set("cryptoAddress", e.target.value)}
              placeholder="0x…"
            />
            <p className="text-xs text-muted-foreground">
              ERC20 only. Sending to an address on another network means the funds are lost, and
              network fees are deducted from the payout.
            </p>
          </div>
        </div>
      )}


      <div className="rounded-lg border border-dashed p-4">
        {!verified ? (
          <div className="flex flex-wrap items-end gap-3">
            {!otpSent ? (
              <Button type="button" onClick={() => sendCode.mutate()} disabled={sendCode.isPending}>
                {sendCode.isPending ? "Sending…" : "Send verification code"}
              </Button>
            ) : (
              <>
                <div className="space-y-2">
                  <Label htmlFor="otp">Email code</Label>
                  <Input
                    id="otp"
                    className="w-36"
                    inputMode="numeric"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                  />
                </div>
                <Button type="button" onClick={() => verify.mutate()} disabled={verify.isPending}>
                  {verify.isPending ? "Verifying…" : "Verify"}
                </Button>
                <Button type="button" variant="ghost" onClick={() => sendCode.mutate()}>
                  Resend
                </Button>
              </>
            )}
            <p className="text-xs text-muted-foreground">
              For your security, payout details can only be changed after email verification.
            </p>
          </div>
        ) : (
          <Button type="button" variant="hot" onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? "Saving…" : "Save payout method"}
          </Button>
        )}
      </div>
    </section>
  );
}

function Field({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <Input value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}
